"use client";

import { Loader2 } from "lucide-react";
import { useTranslations } from "next-intl";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import { useAppState } from "@/app/hooks/useAppState";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetSection,
  SheetTitle,
} from "@/components/ui/sheet";
import { trackIngredient } from "@/lib/analytics";
import {
  categorizeIngredient,
  parseQuantity,
  processIngredientText,
} from "@/lib/categorization/utils";
import { buildIngredientAdditions, type ExistingItem } from "@/lib/recipes/addToList";
import { loadPendingOperations, savePendingOperations, saveState } from "@/lib/storage";
import { createTimestamp } from "@/lib/sync/operations";
import type { AppState, Category, IngredientList, Operation } from "@/lib/types";
import { cn } from "@/lib/utils";

interface AddToListSheetProps {
  isOpen: boolean;
  onClose: () => void;
  recipeTitle: string;
  ingredientLines: string[];
}

interface ParsedLine {
  raw: string;
  text: string;
  quantity: string | undefined;
  category: Category;
}

const CATEGORY_BADGE: Record<Category, string> = {
  meat: "bg-category-meat-bg text-category-meat-text",
  seafood: "bg-category-seafood-bg text-category-seafood-text",
  produce: "bg-category-produce-bg text-category-produce-text",
  fruit: "bg-category-fruit-bg text-category-fruit-text",
  dairy: "bg-category-dairy-bg text-category-dairy-text",
  other: "bg-category-other-bg text-category-other-text",
};

export function AddToListSheet({
  isOpen,
  onClose,
  recipeTitle,
  ingredientLines,
}: AddToListSheetProps) {
  const t = useTranslations("addToList");
  const tCat = useTranslations("categories");
  const { state, setState, queueOperation } = useAppState();
  const [busy, setBusy] = useState(false);

  const lists = useMemo(() => state.lists.filter((l) => !l.isDisabled), [state.lists]);
  const defaultListId = state.activeListId ?? lists[0]?.id ?? null;
  const [targetListId, setTargetListId] = useState<string | null>(defaultListId);

  useEffect(() => {
    if (isOpen) setTargetListId(defaultListId);
  }, [isOpen, defaultListId]);

  const parsedLines = useMemo<ParsedLine[]>(() => {
    return ingredientLines
      .map((raw) => raw.trim())
      .filter((raw) => raw.length > 0)
      .map((raw) => {
        const quantity = parseQuantity(raw);
        const text = processIngredientText(raw, quantity);
        const category = categorizeIngredient(raw);
        return { raw, text, quantity, category };
      });
  }, [ingredientLines]);

  const [selected, setSelected] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (isOpen) {
      // Default: all selected when sheet opens.
      setSelected(new Set(parsedLines.map((_, i) => i)));
    }
  }, [isOpen, parsedLines]);

  const targetList = lists.find((l) => l.id === targetListId) ?? null;
  const allSelected = selected.size === parsedLines.length && parsedLines.length > 0;
  const selectedCount = selected.size;

  const toggleOne = (i: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  };

  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(parsedLines.map((_, i) => i)));
  };

  const handleSubmit = () => {
    if (!targetList || busy || selectedCount === 0) return;
    setBusy(true);

    const linesToAdd = parsedLines.filter((_, i) => selected.has(i)).map((p) => p.raw);

    const existing: ExistingItem[] = targetList.ingredients
      .filter((i) => !i.completed)
      .map((i) => ({
        id: i.id,
        text: i.text,
        quantity: i.quantity,
        category: i.category,
      }));

    const { newIngredients, updatesToExisting } = buildIngredientAdditions(linesToAdd, existing);

    const cappedNew = newIngredients;

    const totalChanged = cappedNew.length + updatesToExisting.length;
    if (totalChanged === 0) {
      toast.info(t("nothingToAdd"));
      setBusy(false);
      onClose();
      return;
    }

    const newState: AppState = {
      ...state,
      lists: state.lists.map((l) => {
        if (l.id !== targetList.id) return l;
        const merged = l.ingredients.map((ing) => {
          const u = updatesToExisting.find((x) => x.id === ing.id);
          return u ? { ...ing, quantity: u.newQuantity } : ing;
        });
        return { ...l, ingredients: [...merged, ...cappedNew] };
      }),
    };
    setState(newState);

    const timestamp = createTimestamp();
    const newOps: Operation[] = [
      ...updatesToExisting.map(
        (u) =>
          ({
            type: "UPDATE_INGREDIENT",
            id: u.id,
            changes: { quantity: u.newQuantity },
            timestamp,
          }) as Operation
      ),
      ...cappedNew.map(
        (ingredient) =>
          ({
            type: "ADD_INGREDIENT",
            listId: targetList.id,
            ingredient,
            timestamp,
          }) as Operation
      ),
    ];
    newOps.forEach((op) => queueOperation(op));

    // Persist synchronously so data survives if component unmounts
    // before React effects fire (batched state updates + onClose).
    saveState(newState);
    savePendingOperations([...loadPendingOperations(), ...newOps]);

    if (cappedNew.length > 0) {
      trackIngredient("added", {
        count: cappedNew.length,
        hasQuantity: cappedNew.some((i) => !!i.quantity),
      });
    }
    if (updatesToExisting.length > 0) {
      trackIngredient("edited", { count: updatesToExisting.length, hasQuantity: true });
    }

    toast.success(t("added", { count: totalChanged, list: targetList.name }));
    setBusy(false);
    onClose();
  };

  return (
    <Sheet open={isOpen} onOpenChange={(open) => (!open && !busy ? onClose() : undefined)}>
      <SheetContent disableOutsideClick={busy}>
        <SheetHeader>
          <SheetTitle>{t("title", { recipe: recipeTitle })}</SheetTitle>
          <SheetDescription className="sr-only">
            {t("fromRecipe", { recipe: recipeTitle })}
          </SheetDescription>
        </SheetHeader>

        {lists.length === 0 ? (
          <p className="px-3 py-4 text-sm text-muted-foreground">{t("noLists")}</p>
        ) : (
          <>
            {/* List selector — fixed */}
            <SheetSection>
              <Select
                value={targetListId ?? undefined}
                onValueChange={(v) => setTargetListId(v)}
                disabled={lists.length <= 1}
              >
                <SelectTrigger className="h-auto min-h-12 w-full justify-start gap-3 rounded-xl px-3 py-2 text-left">
                  <SelectValue placeholder={t("targetListPlaceholder")} className="flex-1" asChild>
                    {targetList ? (
                      <ListRow list={targetList} t={t} />
                    ) : (
                      <span>{t("targetListPlaceholder")}</span>
                    )}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {lists.map((l) => (
                    <SelectItem key={l.id} value={l.id} className="py-2">
                      <div className="flex items-center gap-2">
                        <span
                          aria-hidden
                          className="size-2.5 shrink-0 rounded-full"
                          style={{ backgroundColor: l.color }}
                        />
                        <span className="font-medium">{l.name}</span>
                        <span className="text-xs text-muted-foreground">
                          ·{" "}
                          {t("activeCount", {
                            count: l.ingredients.filter((i) => !i.completed).length,
                          })}
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </SheetSection>

            {/* Select/deselect toggle — fixed */}
            <SheetSection className="flex justify-end">
              <button
                type="button"
                onClick={toggleAll}
                className="cursor-pointer text-xs font-medium text-primary hover:underline"
              >
                {allSelected ? t("deselectAll") : t("selectAll")}
              </button>
            </SheetSection>

            {/* Ingredient list — scrollable */}
            <ul className="min-h-0 flex-1 flex-col overflow-y-auto px-3 sm:px-6 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {parsedLines.map((line, i) => {
                const checked = selected.has(i);
                return (
                  <li
                    key={`${i}-${line.raw}`}
                    className={cn(
                      "flex items-center gap-3 border-b border-border/60 py-2.5 last:border-b-0"
                    )}
                  >
                    <Checkbox
                      id={`ing-${i}`}
                      checked={checked}
                      onCheckedChange={() => toggleOne(i)}
                      aria-label={line.text}
                    />
                    <label
                      htmlFor={`ing-${i}`}
                      className="flex flex-1 cursor-pointer items-center gap-2 text-sm"
                    >
                      {line.quantity && (
                        <span className="shrink-0 font-medium tabular-nums text-foreground">
                          {line.quantity}
                        </span>
                      )}
                      <span className="flex-1 truncate">{line.text}</span>
                    </label>
                    <Badge
                      variant="secondary"
                      className={cn(
                        "shrink-0 border-transparent text-[10px] font-medium uppercase tracking-wide",
                        CATEGORY_BADGE[line.category]
                      )}
                    >
                      {tCat(line.category)}
                    </Badge>
                  </li>
                );
              })}
            </ul>
          </>
        )}

        {lists.length > 0 && (
          <SheetFooter>
            <Button
              type="button"
              size="lg"
              className="w-full"
              disabled={!targetList || selectedCount === 0 || busy}
              onClick={handleSubmit}
            >
              {busy && <Loader2 className="mr-2 size-4 animate-spin" aria-hidden />}
              {targetList
                ? t("ctaAddToList", { count: selectedCount, list: targetList.name })
                : t("ctaSelectList")}
            </Button>
          </SheetFooter>
        )}
      </SheetContent>
    </Sheet>
  );
}

function ListRow({
  list,
  t,
}: {
  list: IngredientList;
  t: ReturnType<typeof useTranslations<"addToList">>;
}) {
  const activeCount = list.ingredients.filter((i) => !i.completed).length;
  return (
    <div className="flex min-w-0 flex-1 items-center gap-2.5">
      <span
        aria-hidden
        className="size-2.5 shrink-0 rounded-full"
        style={{ backgroundColor: list.color }}
      />
      <div className="flex min-w-0 flex-col">
        <span className="truncate text-sm font-medium">{list.name}</span>
        <span className="truncate text-xs text-muted-foreground">
          {t("activeCount", { count: activeCount })}
        </span>
      </div>
    </div>
  );
}
